import { _t } from '@/locale';
import { classnames, isSistaminuten } from '@/helpers';
import { useAppDispatch } from '@/hooks';
import { modalActions } from '@/actions/modalActions';
import { FormState } from '@/reducers/formReducer';

import Avatar from '@/components/elements/Avatar';
import Label from '@/components/elements/Label/Label';
import { RatingV2 } from '@/components/elements/redesign/RatingV2/RatingV2';
import Card from '@/components/elements/Card/Card';
import { STRETCHED_BUTTON } from '@/components/elements/Card/Card.constants';
import Icon from '@/components/icons/Icon';
import { isBookableNormalizedPlace } from '@/helpers';
import { Button } from '@/components/elements/forms/buttons';

import { useSelector } from 'react-redux';
import { NormalizedPlace } from '@/types/place';
import { NormalizedEmployee } from '@/types/employee';
import { ReviewsState } from '@/reducers/reviewsReducer';
import { TIME_FILTER } from '@/constants/timeConstants';
import { MINIMUM_AMOUNT_OF_REVIEWS_REQUIRED } from '@/constants';

type EmployeeCardProps = {
  employeeId: number;
  placeId: number;
  reviewsUid: string;
  handleOnPickEmployee: (employee: NormalizedEmployee) => void;
};

/**
 * Employee card on the place page, rendered when both reviews-v2 and listcards-v2 are enabled.
 * Legacy variants for the other flag combinations: LegacyEmployeeListCardV1/V2/V3.
 */
export const EmployeeListCard = ({ handleOnPickEmployee, employeeId, placeId, reviewsUid }: EmployeeCardProps) => {
  const reduxDispatch = useAppDispatch();

  const employee = useSelector(
    (state: { employees: { all: Record<number, NormalizedEmployee> } }) => state.employees.all[employeeId],
  );
  const employeeReviews = useSelector(
    (state: { reviews: ReviewsState }) => state.reviews?.byUid?.[reviewsUid]?.details?.employeeStats?.[employeeId],
  );

  const place = useSelector((state: { place: Record<number, NormalizedPlace> }) => state.place[placeId]);

  const timeFilterFromState = useSelector(
    (state: { form: FormState }) => state.form[reviewsUid]?.searchFilter?.timeFilter,
  );

  if (!employee?.about || employee.about.inactive) {
    return null;
  }

  const { name, jobTitle, settings, certificates, avatar } = employee.about || {};

  const employeeJobTitle = (jobTitle || '').split('.').join('. ');

  const hasCertificate = certificates?.length || false;
  const acceptsGiftcard = !isSistaminuten() && settings?.acceptsGiftCard;
  const hasBadges = acceptsGiftcard || hasCertificate;

  const showBookButton = isBookableNormalizedPlace({ employee, place });

  const onShowReviewModal = () => {
    reduxDispatch(
      modalActions.reviews({
        show: true,
        placeId,
        employeeId,
        serviceId: undefined,
        timeFilter: timeFilterFromState || TIME_FILTER.FOREVER,
        focus: 'employee',
        context: 'placePage',
      }),
    );
  };

  const renderAvatar = (className: string) => (
    <div className={className}>
      <Avatar src={settings?.showPhoto === false ? null : avatar} size={'md'} />
    </div>
  );

  const renderBadges = () => {
    if (!hasBadges) {
      return null;
    }

    return (
      <div className="gap-sm @[600px]:hidden flex flex-wrap">
        {hasCertificate && (
          <a href="#certificates" className="relative z-10">
            <Label variant="certificate" />
          </a>
        )}
        {acceptsGiftcard && <Label variant="giftcard" />}
      </div>
    );
  };

  const renderDetails = () => (
    <div className="gap-sm flex items-center">
      {renderAvatar('flex @[600px]:hidden')}
      <header className="min-w-0 flex-1">
        <h3 className="text-l text-fg_primary">
          <button type="button" onClick={onShowReviewModal} className={classnames(STRETCHED_BUTTON)}>
            {name}
          </button>
        </h3>
        {employeeJobTitle && <p className="text-m text-fg_secondary mt-1 truncate">{employeeJobTitle}</p>}
      </header>
      <div className="text-m gap-2xs text-fg_link flex shrink-0 items-center whitespace-nowrap">
        {_t('moreInfo')}
        <Icon variant="info-circle" color="fg_link" size="xs" />
      </div>
    </div>
  );

  const ratingProps = {
    ratingCount: employeeReviews?.count >= MINIMUM_AMOUNT_OF_REVIEWS_REQUIRED ? employeeReviews?.count : 0,
    averageRating: employeeReviews?.score ?? 0,
    size: 'md' as const,
    withRatingCounter: true,
    withAverageScore:
      employeeReviews?.count >= MINIMUM_AMOUNT_OF_REVIEWS_REQUIRED ? Boolean(employeeReviews?.score) : false,
    withMultipleStars: false,
  };

  const renderRatingColumn = () => (
    <div
      className={
        'border-border_regular group-active:border-border_bold @[600px]:border-l @[600px]:border-t-0 border-t'
      }>
      <div
        className={
          'text-s gap-sm pt-sm @[600px]:pl-sm @[600px]:pt-0 @[600px]:min-w-[190px] @[600px]:justify-end flex h-full items-center justify-between'
        }>
        {/* direction changes RatingV2's markup, not just its classes — one per layout, CSS shows one. */}
        <RatingV2 {...ratingProps} direction="row" className="@[600px]:hidden" />
        <RatingV2 {...ratingProps} direction="column" className="@[600px]:flex hidden" />
        <div className="relative z-10 min-w-[56px]">
          {showBookButton ? (
            <Button
              label={_t('book')}
              onClick={() => {
                handleOnPickEmployee(employee);
              }}
              size={'sm'}
              variant={'primary'}
            />
          ) : null}
        </div>
      </div>
    </div>
  );

  return (
    <Card
      size="sm"
      // The card is the container its @[600px]: variants query — its own width, not the viewport's.
      className="@container hover:drop-shadow-md active:drop-shadow-md"
      cardBackground="group relative bg-surface_l2 border border-transparent hover:border-border_bold active:bg-bg_secondary_hover">
      <div className="gap-sm @[600px]:flex-row flex flex-col">
        <div
          className={classnames('gap-sm flex min-w-0 flex-1', {
            '@[600px]:items-center items-start': hasBadges,
            'items-center': !hasBadges,
          })}>
          {renderAvatar('hidden @[600px]:flex')}
          <div className="gap-xs flex min-w-0 flex-1 flex-col">
            {renderBadges()}
            {renderDetails()}
          </div>
        </div>
        {renderRatingColumn()}
      </div>
    </Card>
  );
};

export default EmployeeListCard;
